import { useEffect, useRef } from 'react';

interface UseScrollToElementProps {
    filterGroups: any[];
    lastAddedGroupRef: React.MutableRefObject<string | null>;
    lastAddedConditionRef: React.MutableRefObject<{ groupId: string; conditionId: string } | null>;
    containerRef: React.RefObject<HTMLDivElement | null>;
}

export const useScrollToElement = ({
    filterGroups,
    lastAddedGroupRef,
    lastAddedConditionRef,
    containerRef,
}: UseScrollToElementProps) => {
    useEffect(() => {
        const scrollToElement = (selector: string) => {
            // Find the parent scroll container (in PatientListSelection)
            const scrollContainer = containerRef.current?.closest('.overflow-y-auto') as HTMLElement;
            const element = containerRef.current?.querySelector(selector) as HTMLElement;

            if (element && scrollContainer) {
                // Calculate the position relative to scroll container using getBoundingClientRect
                const elementRect = element.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const currentScrollTop = scrollContainer.scrollTop;
                const scrollPosition = currentScrollTop + (elementRect.top - containerRect.top) - 20; // 20px padding

                scrollContainer.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth',
                });
            } else if (element) {
                // Fallback: use scrollIntoView if scroll container not found
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        };

        if (lastAddedGroupRef.current) {
            const groupId = lastAddedGroupRef.current;
            lastAddedGroupRef.current = null;

            setTimeout(() => {
                scrollToElement(`[data-group-id="${groupId}"]`);
            }, 100);
        }

        if (lastAddedConditionRef.current) {
            const { conditionId } = lastAddedConditionRef.current;
            lastAddedConditionRef.current = null;

            setTimeout(() => {
                scrollToElement(`[data-condition-id="${conditionId}"]`);
            }, 100);
        }
    }, [filterGroups, lastAddedGroupRef, lastAddedConditionRef, containerRef]);
};
